import React from "react";
import { observer, useLocalObservable } from "mobx-react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Grid2,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  Theme,
} from "@mui/material";
import {
  ProductListApi,
  ProductListSortBy,
} from "../../../../../models/CatalogProducts/types";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { useStores } from "../../../use-stores";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import ImageGalleryPopup from "../../../../../views/components/common/pop-up/ImageGalleryPopup";
import { SortType } from "../../../../../models/SortType";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";

const CELL_SX = { fontSize: "12px", textAlign: "center" as const };
const HEADER_SX = { textAlign: "center" as const, fontSize: 12 };
/** Fixed width — minWidth alone cannot cap growth under table-layout:auto + nowrap cells. */
const NAME_COL_WIDTH = "300px";
const NAME_COL_SX = {
  width: NAME_COL_WIDTH,
  minWidth: NAME_COL_WIDTH,
  maxWidth: NAME_COL_WIDTH,
  overflow: "hidden" as const,
  boxSizing: "border-box" as const,
};

interface GalleryImage {
  id?: number;
  src: string;
  alt?: string;
}

export interface CatalogProductTableProps {
  maxHeight: number | string;
  products: ProductListApi[];
  limit: number;
  page_no: number;
  total_count: number;
  sortBy: ProductListSortBy;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: ProductListSortBy, sortType: SortType) => void;
  onToggleDisplay: (product: ProductListApi) => Promise<void> | void;
  onDelete: (product: ProductListApi) => void;
  onRestore: (product: ProductListApi) => void;
}

function CatalogProductTable({
  maxHeight,
  products,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onToggleDisplay,
  onDelete,
  onRestore,
}: CatalogProductTableProps) {
  const navigate = useNavigate();
  const theme = useTheme<Theme>();
  const { userStore } = useStores();
  const canEdit = userStore.hasPermission(EmployeeRoles.EDIT_PRODUCT);
  const colors = theme.palette.customColors;

  const ui = useLocalObservable(() => ({
    togglingDisplayIds: {} as Record<number, boolean>,
    hoveredProductId: null as number | null,
    galleryOpen: false,
    galleryImages: [] as GalleryImage[],
    galleryTitle: "Product Images",
    setToggling(id: number, value: boolean) {
      this.togglingDisplayIds = { ...this.togglingDisplayIds, [id]: value };
    },
    setHoveredProductId(id: number | null) {
      this.hoveredProductId = id;
    },
    openGallery(product: ProductListApi) {
      try {
        if (!product.default_image_url) return;
        this.galleryImages = [
          {
            id: product.id,
            src: product.default_image_url,
            alt: product.name?.trim() || "Product",
          },
        ];
        this.galleryTitle = product.name?.trim() || "Product Images";
        this.galleryOpen = true;
      } catch {
        // Ignore gallery open failures; row navigation remains available.
      }
    },
    closeGallery() {
      this.galleryOpen = false;
      if (this.galleryImages.length > 0) {
        this.galleryImages = [];
      }
    },
  }));

  const goToDetail = (id: number) => navigate(`/products/${id}`);

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const handleHeaderSort = (column: string, nextSortType: SortType) => {
    onSortChange(column as ProductListSortBy, nextSortType);
  };

  const handleDisplayToggle = async (row: ProductListApi) => {
    if (!canEdit || ui.togglingDisplayIds[row.id]) return;
    ui.setToggling(row.id, true);
    try {
      await onToggleDisplay(row);
    } catch {
      // Parent handles error notification; ensure spinner clears.
    } finally {
      ui.setToggling(row.id, false);
    }
  };

  const handleOpenImageGallery = (
    event: React.MouseEvent,
    product: ProductListApi,
  ) => {
    try {
      event.stopPropagation();
      ui.openGallery(product);
    } catch {
      // Ignore; prevent row navigation only when gallery opens successfully.
    }
  };

  const formatMoney = (n: number) => {
    if (!Number.isFinite(n)) return "-";
    return `₹${n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatThumbnailUrl = (url: string | null) => {
    if (!url) return undefined;
    return `${url}?width=100&height=100&format=webp`;
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      {products.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight }}>
            <Table
              stickyHeader
              sx={{
                // Required so width/maxWidth on Name are honored; auto layout
                // ignores max-width on cells and expands to nowrap content.
                tableLayout: "fixed",
                minWidth: 1190,
              }}
            >
              <TableHead sx={{ position: "relative", zIndex: 30 }}>
                <TableRow>
                  <AppTableHeaderCell
                    title="Name"
                    fontSize="12px"
                    minWidth={NAME_COL_WIDTH}
                    sortColumn="name"
                    sortBy={sortBy}
                    sortDirection={sortType}
                    onSortChange={handleHeaderSort}
                    sx={{
                      ...HEADER_SX,
                      ...NAME_COL_SX,
                      position: "sticky",
                      left: 0,
                      top: 0,
                      zIndex: 30,
                      backgroundColor: theme.palette.background.paper,
                      isolation: "isolate",
                    }}
                  />
                  <AppTableHeaderCell
                    title="Category"
                    fontSize="12px"
                    minWidth="120px"
                    sx={{ ...HEADER_SX, minWidth: "120px" }}
                  />
                  <AppTableHeaderCell
                    title="SKU"
                    fontSize="12px"
                    minWidth="120px"
                    sx={{ ...HEADER_SX, minWidth: "120px" }}
                  />
                  <AppTableHeaderCell
                    title="Cost"
                    fontSize="12px"
                    minWidth="100px"
                    sortColumn="cost_price"
                    sortBy={sortBy}
                    sortDirection={sortType}
                    onSortChange={handleHeaderSort}
                    sx={{ ...HEADER_SX, minWidth: "100px" }}
                  />
                  <AppTableHeaderCell
                    title="Selling"
                    fontSize="12px"
                    minWidth="100px"
                    sortColumn="selling_price"
                    sortBy={sortBy}
                    sortDirection={sortType}
                    onSortChange={handleHeaderSort}
                    sx={{ ...HEADER_SX, minWidth: "100px" }}
                  />
                  <AppTableHeaderCell
                    title="HSN"
                    fontSize="12px"
                    minWidth="100px"
                    sx={{ ...HEADER_SX, minWidth: "100px" }}
                  />
                  <AppTableHeaderCell
                    title="GST %"
                    fontSize="12px"
                    minWidth="80px"
                    sx={{ ...HEADER_SX, minWidth: "80px" }}
                  />
                  <AppTableHeaderCell
                    title="Active"
                    fontSize="12px"
                    minWidth="100px"
                    sortColumn="active"
                    sortBy={sortBy}
                    sortDirection={sortType}
                    onSortChange={handleHeaderSort}
                    sx={{ ...HEADER_SX, minWidth: "100px" }}
                  />
                  <AppTableHeaderCell
                    title="Display"
                    fontSize="12px"
                    minWidth="100px"
                    sortColumn="display"
                    sortBy={sortBy}
                    sortDirection={sortType}
                    onSortChange={handleHeaderSort}
                    sx={{ ...HEADER_SX, minWidth: "100px" }}
                  />
                  <AppTableHeaderCell
                    title="Created"
                    fontSize="12px"
                    minWidth="160px"
                    sortColumn="created_date"
                    sortBy={sortBy}
                    sortDirection={sortType}
                    onSortChange={handleHeaderSort}
                    sx={{ ...HEADER_SX, minWidth: "160px" }}
                  />
                  <AppTableHeaderCell
                    title="Updated"
                    fontSize="12px"
                    minWidth="160px"
                    sortColumn="modified_date"
                    sortBy={sortBy}
                    sortDirection={sortType}
                    onSortChange={handleHeaderSort}
                    sx={{ ...HEADER_SX, minWidth: "160px" }}
                  />
                  <AppTableHeaderCell
                    title="Actions"
                    fontSize="12px"
                    minWidth="80px"
                    sx={{ ...HEADER_SX, minWidth: "80px" }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((row) => {
                  const displayLoading = Boolean(ui.togglingDisplayIds[row.id]);
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:nth-of-type(odd)": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => goToDetail(row.id)}
                    >
                      <AppTableRowCell
                        sx={{
                          ...CELL_SX,
                          ...NAME_COL_SX,
                          left: 0,
                          position: "sticky",
                          zIndex: 10,
                          boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3)",
                          backgroundColor: theme.palette.background.paper,
                          textAlign: "left",
                          cursor: "pointer",
                          whiteSpace: "normal",
                          "&:hover .catalog-product-name": {
                            color: colors?.purpleMain,
                          },
                        }}
                      >
                        <Grid2
                          container
                          alignItems="center"
                          wrap="nowrap"
                          spacing={1}
                          sx={{
                            minWidth: 0,
                            width: "100%",
                            maxWidth: "100%",
                            overflow: "hidden",
                          }}
                        >
                          <Grid2 size="auto" sx={{ flexShrink: 0 }}>
                            <Box
                              sx={{ position: "relative", flexShrink: 0 }}
                              onMouseEnter={() =>
                                ui.setHoveredProductId(row.id)
                              }
                              onMouseLeave={() => ui.setHoveredProductId(null)}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Avatar
                                src={
                                  row.default_image_url
                                    ? formatThumbnailUrl(row.default_image_url)
                                    : undefined
                                }
                                variant="rounded"
                                sx={{ width: 40, height: 40 }}
                              >
                                {row.name?.charAt(0)?.toUpperCase() || "P"}
                              </Avatar>
                              {row.default_image_url &&
                                ui.hoveredProductId === row.id && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: "100%",
                                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                                      borderRadius: "4px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      cursor: "pointer",
                                    }}
                                    onClick={(e) =>
                                      handleOpenImageGallery(e, row)
                                    }
                                    aria-label={`View images for ${row.name}`}
                                  >
                                    <VisibilityOutlinedIcon
                                      sx={{
                                        color: colors?.white || "#fff",
                                        fontSize: 16,
                                      }}
                                    />
                                  </Box>
                                )}
                            </Box>
                          </Grid2>
                          <Grid2
                            size="grow"
                            sx={{
                              minWidth: 0,
                              maxWidth: "100%",
                              overflow: "hidden",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 0.25,
                            }}
                          >
                            <SkuCopyRow
                              sku={row.name}
                              emptyFallback="-"
                              copyLabel="Copy name"
                              className="catalog-product-name"
                              fontWeight={700}
                              color={theme.palette.text.primary}
                              allowWrap
                              typographySx={{
                                textDecoration: "underline",
                              }}
                            />
                            <CustomTypography
                              variant="caption"
                              color={colors?.greyTextColor}
                              sx={{
                                fontWeight: 400,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "100%",
                              }}
                            >
                              #{row.id}
                            </CustomTypography>
                          </Grid2>
                        </Grid2>
                      </AppTableRowCell>
                      <AppTableRowCell sx={CELL_SX}>
                        {row.category_name?.trim() || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell sx={CELL_SX}>
                        {row.sku?.trim() || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell sx={CELL_SX}>
                        {formatMoney(row.cost_price)}
                      </AppTableRowCell>
                      <AppTableRowCell sx={CELL_SX}>
                        {formatMoney(row.selling_price)}
                      </AppTableRowCell>
                      <AppTableRowCell sx={CELL_SX}>
                        <SkuCopyRow
                          sku={row.hsn_code}
                          emptyFallback="-"
                          copyLabel="Copy HSN"
                        />
                      </AppTableRowCell>
                      <AppTableRowCell sx={CELL_SX}>
                        {row.gst_percentage != null &&
                        Number.isFinite(row.gst_percentage)
                          ? `${row.gst_percentage}%`
                          : "-"}
                      </AppTableRowCell>
                      <AppTableRowCell sx={CELL_SX}>
                        <Chip
                          size="small"
                          label={row.active ? "Active" : "Inactive"}
                          color={row.active ? "success" : "error"}
                          variant="outlined"
                          sx={{ fontSize: "11px", height: 22 }}
                        />
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={CELL_SX}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canEdit ? (
                          <Tooltip
                            title={
                              row.display
                                ? "Hide on storefront"
                                : "Show on storefront"
                            }
                          >
                            <Box
                              component="span"
                              onClick={() => void handleDisplayToggle(row)}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                cursor: displayLoading ? "wait" : "pointer",
                              }}
                            >
                              {displayLoading ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Switch
                                  checked={row.display}
                                  size="small"
                                  color="primary"
                                  sx={{ pointerEvents: "none" }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Chip
                            size="small"
                            label={row.display ? "Shown" : "Hidden"}
                            color={row.display ? "success" : "error"}
                            variant="outlined"
                            sx={{ fontSize: "11px", height: 22 }}
                          />
                        )}
                      </AppTableRowCell>
                      <AppTableRowCell sx={CELL_SX}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.25,
                            minWidth: 0,
                          }}
                        >
                          <CustomTypography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.created_by_name?.trim() || "-"}
                          </CustomTypography>
                          <CustomTypography
                            variant="body3"
                            color="text.secondary"
                          >
                            {row.created_date
                              ? formatDate(row.created_date)
                              : "-"}
                          </CustomTypography>
                        </Box>
                      </AppTableRowCell>
                      <AppTableRowCell sx={CELL_SX}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.25,
                            minWidth: 0,
                          }}
                        >
                          <CustomTypography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.last_modified_by_name?.trim() || "-"}
                          </CustomTypography>
                          <CustomTypography
                            variant="body3"
                            color="text.secondary"
                          >
                            {row.modified_date
                              ? formatDate(row.modified_date)
                              : "-"}
                          </CustomTypography>
                        </Box>
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={CELL_SX}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          {canEdit ? (
                            row.active ? (
                              <Tooltip title="Deactivate product">
                                <IconButton
                                  size="small"
                                  onClick={() => onDelete(row)}
                                  sx={{
                                    color: colors?.errorRed,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.action.hover,
                                    },
                                  }}
                                  aria-label={`Deactivate ${row.name}`}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Restore product">
                                <IconButton
                                  size="small"
                                  onClick={() => onRestore(row)}
                                  sx={{
                                    color:
                                      colors?.purpleMain ||
                                      theme.palette.primary.main,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.action.hover,
                                    },
                                  }}
                                  aria-label={`Restore ${row.name}`}
                                >
                                  <RestoreFromTrashIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )
                          ) : (
                            "-"
                          )}
                        </Box>
                      </AppTableRowCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
      {ui.galleryOpen ? (
        <ImageGalleryPopup
          key="catalog-product-images"
          open={ui.galleryOpen}
          images={ui.galleryImages}
          title={ui.galleryTitle}
          onClose={() => ui.closeGallery()}
        />
      ) : null}
    </Paper>
  );
}

export default observer(CatalogProductTable);
